// Payments card calculations for the customer record (2026-10-11).
//
// GET /stripe/transactions returns { items } with grossPence (pence) and a
// description "… · <quote ref> — <customer>"; the card used to read
// `transactions` and `amount`, which never existed, so Paid was always £0.
// Paid per ordered quote now comes from the payment schedule's server total
// (card stages + hand-recorded payments) where a schedule exists.

export interface WireTransaction {
  id: string;
  amount: number;
  currency: string;
  status: string;
  description?: string;
  customerName?: string;
  customerId?: string;
  quoteRef?: string;
  createdAt: number;
}

/** A row as GET /stripe/transactions actually returns it. */
export interface WireStripeItem {
  id: string;
  amount?: number;
  grossPence?: number;
  currency?: string;
  status: string;
  description?: string;
  customerName?: string;
  customerId?: string;
  quoteRef?: string;
  createdAt: number;
}

/** Pounds, quote ref and customer name from a stored Stripe row. */
export function normaliseStripeItem(t: WireStripeItem): WireTransaction {
  const desc = t.description || "";
  const ref = t.quoteRef || (/·\s*(KOS-Q-[A-Z0-9-]+)/.exec(desc) || [])[1] || undefined;
  const name = t.customerName || (/—\s*(.+)$/.exec(desc) || [])[1]?.trim() || undefined;
  const amount = typeof t.amount === "number" ? t.amount : (Number(t.grossPence) || 0) / 100;
  return {
    id: t.id, amount, currency: t.currency || "gbp", status: t.status, description: desc.replace(/\s*—\s*.+$/, ""),
    ...(name ? { customerName: name } : {}), ...(t.customerId ? { customerId: t.customerId } : {}),
    ...(ref ? { quoteRef: ref } : {}), createdAt: t.createdAt,
  };
}

/** Is this Stripe row this customer's? By quote ref first, then id or name. */
export function txBelongsTo(t: WireTransaction, customerId: string, customerName: string, quoteRefs: Set<string>): boolean {
  if (t.quoteRef && quoteRefs.has(t.quoteRef)) return true;
  if (t.customerId && t.customerId === customerId) return true;
  const n = customerName.trim().toLowerCase();
  return !!n && (t.customerName || "").trim().toLowerCase() === n;
}

export const isSucceeded = (t: WireTransaction): boolean => t.status === "succeeded" || t.status === "paid";

/** Paid per ordered quote: the schedule's own total where one exists, else matched Stripe rows. */
export function paidForQuote(ref: string, scheduleTotals: Map<string, number>, txs: WireTransaction[]): number {
  const fromSchedule = scheduleTotals.get(ref);
  if (typeof fromSchedule === "number") return fromSchedule;
  return txs.filter((t) => t.quoteRef === ref && isSucceeded(t)).reduce((sum, t) => sum + (t.amount || 0), 0);
}
/** Confirmation wording before an auto-send date is saved: what, to whom, when. */
export function autoSendConfirmText(amount: number, stageLabel: string, customerName: string, dateIso: string, todayIso = new Date().toISOString().slice(0, 10)): string {
  const money = `£${amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const [y, m, d] = dateIso.split("-");
  const when = y && m && d ? `${d}/${m}/${y}` : dateIso;
  const soon = dateIso <= todayIso
    ? " That date is today or earlier, so it will go at the next send, which can be within minutes."
    : " It goes out with the morning send (09:00 UTC) on that day.";
  return `Send ${customerName} a payment request for ${money} (${stageLabel}) automatically on ${when}? They'll get a text, an email and a portal message.${soon}`;
}
